import { useRouter } from "@uirouter/react";
import { noop } from "lodash";
import { FC, useCallback } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Button } from "../../../../../components/button";
import { TextField } from "../../../../../components/forms/text";
import { useCopying } from "../../../../../components/hooks/use-copying";
import { Icon } from "../../../../../components/icons";
import { Flex, FullSize } from "../../../../../components/layout";
import { useAppLoader } from "../../../../../components/loader/app-loader-hook";
import { WIDGET_BORDER_RADIUS } from "../../../../../components/widget";
import { BillingDTO } from "../../../../../store/billing/models/dto";
import { BillingService } from "../../../../../store/billing/service";
import { colours } from "../../../../../styles/colours";
import { useYupValidationResolver } from "../../../../../utils/utils";
import { BillingRoutes } from "../../../routes";

const Address = styled(Flex)`
  line-break: anywhere;
  background: ${colours.lightBlue};
  padding: 1em;
  border-radius: ${WIDGET_BORDER_RADIUS};
  width: 100%;
  font-family: monospace;
  height: 6em;
  justify-content: space-between;
`;

const Copied = styled(Address)`
  background: ${colours.green};
  color: ${colours.white};
  font-family: inherit;
  font-weight: 700;
  justify-content: center;
`;

const CopyIcon = styled(Icon).attrs({ type: "copy" })`
  display: inline-block;
  margin-left: 1em;
`;

const StyledForm = styled(Flex).attrs({ as: "form" })`
  ${FullSize};
  flex-direction: column;
  justify-content: space-around;
  align-items: center;
`;

export const Confirm: FC<{
  purchaseID: string;
  chain: BillingDTO.Responses.GetSKRTAddresses.T[number];
}> = ({ purchaseID, chain }) => {
  const loader = useAppLoader();
  const router = useRouter();

  const [isCopying, setCopying] = useCopying(chain?.address || "");
  const { validation } = BillingDTO.Requests.ConfirmPurchase.useFields();
  const resolver = useYupValidationResolver(validation);
  const {
    handleSubmit,
    control,
  } = useForm<BillingDTO.Requests.ConfirmPurchase.T>({
    resolver,
    defaultValues: {
      sekuritanceWallet: chain,
      transactionHash: "",
    },
  });

  const { t } = useTranslation("billing");

  const onSubmit = useCallback(
    (data: BillingDTO.Requests.ConfirmPurchase.T) =>
      loader(
        BillingService.confirmPurchase(purchaseID, data)
          .then(() =>
            router.stateService.go(BillingRoutes.names.result, {
              transactionHash: data.transactionHash,
              purchaseID,
            })
          )
          .catch(noop)
      ),
    [loader, purchaseID]
  );

  return (
    <StyledForm onSubmit={handleSubmit(onSubmit)}>
      <div>{t("send-amount-to")}:</div>
      {isCopying ? (
        <Copied>{t("address-copied")}!</Copied>
      ) : (
        <Address onClick={setCopying}>
          {chain.address} <CopyIcon />
        </Address>
      )}
      <div>{t("and-note-the-transaction-hash")}:</div>
      <Controller
        control={control}
        name="transactionHash"
        render={({ field, fieldState: { error } }) => (
          <TextField
            {...field}
            fullWidth
            label={t("transaction-hash")}
            helperText={error?.message}
            error={!!error}
          />
        )}
      />
      <Button type="submit">{t("submit", { ns: "common" })}</Button>
    </StyledForm>
  );
};
